<script setup lang="ts">
import { AlertCircle, Bell, Check, CheckCheck, Clock, Star } from 'lucide-vue-next'
import { computed } from 'vue'

import { cn } from '@/lib/utils'

interface Props {
  id: number
  direction: 'outbound' | 'inbound'
  status: 'pending' | 'sent' | 'delivered' | 'failed'
  messageType?: 'chat' | 'transactional' | 'review_invite'
  body: string
  sentAt: string
  senderName?: string | null
  isGrouped?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isGrouped:   false,
  messageType: 'chat',
})

const emit = defineEmits<{
  retry: [id: number]
}>()

const isOutbound       = computed(() => props.direction === 'outbound')
const isReviewInvite   = computed(() => props.messageType === 'review_invite')
const isTransactional  = computed(() => props.messageType === 'transactional')

const timeLabel = computed(() => {
  try {
    const date = new Date(props.sentAt)
    const now = Date.now()
    const diffMs = now - date.getTime()
    const diffMin = Math.floor(diffMs / 60_000)
    const diffHr  = Math.floor(diffMs / 3_600_000)
    if (diffMin < 1)  return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr  < 24) return `${diffHr}h ago`
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
})

const exactTime = computed(() => {
  try {
    return new Date(props.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
})
</script>

<template>
  <div
    :class="cn(
      'flex w-full',
      isOutbound ? 'justify-end' : 'justify-start',
      isGrouped ? 'mt-0.5' : 'mt-3',
    )"
  >
    <!-- ── Review invite bubble ─────────────────────────────────────────────── -->
    <div
      v-if="isReviewInvite"
      class="group relative max-w-[80%] rounded-2xl rounded-br-sm border border-yellow-300 bg-yellow-50 px-3.5 py-2 text-sm dark:border-yellow-700 dark:bg-yellow-950/30"
    >
      <div class="flex items-center gap-1.5 text-yellow-700 dark:text-yellow-400">
        <Star class="size-3.5 fill-yellow-500 text-yellow-500" />
        <span class="italic font-medium">Review request sent</span>
      </div>
      <div class="mt-1 flex items-center gap-1 text-[10px] justify-end opacity-70 text-yellow-700 dark:text-yellow-400">
        <span :title="exactTime">{{ timeLabel }}</span>
        <Check class="size-3" />
      </div>
    </div>

    <!-- ── Regular / transactional bubble ──────────────────────────────────── -->
    <div
      v-else
      :class="cn(
        'group relative max-w-[80%] rounded-2xl px-3.5 py-2 text-sm',
        isOutbound
          ? 'rounded-br-sm bg-primary text-primary-foreground'
          : 'rounded-bl-sm bg-muted text-foreground',
        status === 'failed' && 'bg-destructive/15 text-destructive ring-1 ring-destructive/30',
      )"
    >
      <!-- Sender label (inbound only, when not grouped) -->
      <p
        v-if="!isOutbound && !isGrouped && senderName"
        class="mb-0.5 text-xs font-medium opacity-70"
      >
        {{ senderName }}
      </p>

      <!-- Transactional badge -->
      <div
        v-if="isTransactional && !isGrouped"
        class="mb-1 flex items-center gap-1 opacity-60"
      >
        <Bell class="size-3" />
        <span class="text-[10px] italic">System message</span>
      </div>

      <!-- Message body -->
      <p class="whitespace-pre-wrap break-words leading-relaxed">{{ body }}</p>

      <!-- Footer: time + status -->
      <div
        :class="cn(
          'mt-1 flex items-center gap-1 text-[10px]',
          isOutbound ? 'justify-end opacity-70' : 'opacity-60',
        )"
      >
        <span :title="exactTime">{{ timeLabel }}</span>

        <!-- Status icon (outbound only) -->
        <template v-if="isOutbound">
          <Clock v-if="status === 'pending'" class="size-3" />
          <Check v-else-if="status === 'sent'" class="size-3" />
          <CheckCheck v-else-if="status === 'delivered'" class="size-3" />
          <AlertCircle v-else-if="status === 'failed'" class="size-3 text-destructive" />
        </template>
      </div>

      <!-- Retry button on failed messages (chat type only) -->
      <button
        v-if="status === 'failed' && messageType === 'chat'"
        class="mt-1 text-[11px] font-medium text-destructive underline-offset-2 hover:underline"
        type="button"
        @click="emit('retry', id)"
      >
        Retry
      </button>
    </div>
  </div>
</template>
